import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useAnimation } from 'framer-motion';

// Enhanced Button Component
export const EnhancedButton = ({ children, className = '', variant = 'primary', ...props }) => {
  const baseClasses = 'font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl';
  
  const variants = {
    primary: 'bg-red-600 hover:bg-red-700 text-white',
    secondary: 'bg-yellow-400 hover:bg-yellow-500 text-black',
    outline: 'border-2 border-white text-white hover:bg-white hover:text-red-600',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100',
  };

  return (
    <motion.button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

// Animated Counter Hook
export const useAnimatedCounter = (end, duration = 2) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  const startAnimation = () => {
    if (hasAnimated) return;
    setHasAnimated(true);
    
    const startTime = Date.now();
    const startValue = 0;
    
    const updateCount = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic function for smoother animation
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(startValue + (end - startValue) * easedProgress);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(updateCount);
      }
    };
    
    requestAnimationFrame(updateCount);
  };

  return { count, startAnimation, hasAnimated };
};

// Animated Counter Component
export const AnimatedCounter = ({ end, prefix = '', suffix = '', className = '' }) => {
  const { count, startAnimation } = useAnimatedCounter(end);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  React.useEffect(() => {
    if (isInView) {
      startAnimation();
    }
  }, [isInView, startAnimation]);

  return (
    <motion.div 
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 20 }}
      transition={{ duration: 0.6 }}
    >
      {prefix}{count.toLocaleString()}{suffix}
    </motion.div>
  );
};

// Fade In Animation Component
export const FadeInWhenVisible = ({ children, direction = 'up', delay = 0, className = '' }) => {
  const controls = useAnimation();
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  React.useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const variants = {
    hidden: { 
      opacity: 0, 
      y: direction === 'up' ? 60 : direction === 'down' ? -60 : 0,
      x: direction === 'left' ? 60 : direction === 'right' ? -60 : 0
    },
    visible: { 
      opacity: 1, 
      y: 0,
      x: 0,
      transition: { 
        duration: 0.8, 
        delay: delay,
        ease: [0.6, -0.05, 0.01, 0.99]
      } 
    }
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={controls}
      variants={variants}
    >
      {children}
    </motion.div>
  );
};

// Navigation items (outside component for performance)
const NAV_ITEMS = [
  { name: 'Home', path: '/' },
  { name: 'Donate', path: '/donate' },
  { name: 'Wholesale', path: '/wholesale' },
  { name: 'Impact', path: '/impact' },
  { name: 'About', path: '/about' },
];

// Header Component
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll function
  const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <motion.header 
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white shadow-md'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <motion.div 
              className="text-black font-semibold text-lg tracking-wide hover:text-red-600 transition-all duration-300 transform hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              SECOND CHANCE CLOTHING
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {NAV_ITEMS.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`text-gray-700 hover:text-red-600 font-medium transition-all duration-300 relative group ${
                    location.pathname === item.path ? 'text-red-600' : ''
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full ${
                    location.pathname === item.path ? 'w-full' : ''
                  }`}></span>
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <motion.button
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              animate={{ rotate: isMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </motion.svg>
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{ height: isMenuOpen ? 'auto' : 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden"
        >
          <motion.div 
            className="pb-4 space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: isMenuOpen ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {NAV_ITEMS.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: isMenuOpen ? 0 : -20, opacity: isMenuOpen ? 1 : 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className="block py-2 px-3 text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </motion.header>
  );
};

// Footer Component
export const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-black text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="mb-6">
              <motion.h2 
                className="text-white font-bold text-2xl mb-2"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                SECOND CHANCE CLOTHING
              </motion.h2>
              <div className="text-yellow-400 text-sm font-medium">Clothing for community & planet</div>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              Rescuing quality garments and giving them a second life. Reducing waste, supporting people, 
              and inspiring environmental stewardship, one stitch at a time.
            </p>
            
            {/* Contact Information */}
            <div className="space-y-4 mb-6">
              {/* Address - Simplified */}
              <motion.div 
                className="text-gray-300"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-yellow-400 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="text-left">
                    <motion.a
                      href="https://maps.google.com/?q=Port+of+SF+Pier+50+Shed+A+Bays+28-29+San+Francisco+CA+94158"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-yellow-400 transition-colors duration-300 block text-left"
                      whileHover={{ scale: 1.02 }}
                      aria-label="View our location on Google Maps"
                    >
                      Port of SF, Pier 50, Shed A, Bays 28-29<br />
                      San Francisco, CA 94158
                    </motion.a>
                  </div>
                </div>
              </motion.div>

              {/* Email - Simplified */}
              <motion.div 
                className="text-gray-300"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <motion.a
                    href="mailto:contact@secondchanceclothing.org"
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-300"
                    whileHover={{ scale: 1.02 }}
                    aria-label="Send us an email"
                  >
                    contact@secondchanceclothing.org
                  </motion.a>
                </div>
              </motion.div>

              {/* Phone Number - New Addition */}
              <motion.div 
                className="text-gray-300"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <motion.a
                    href="tel:+18663854765"
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-300"
                    whileHover={{ scale: 1.02 }}
                    aria-label="Call us toll-free"
                  >
                    1-866-385-4765
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Quick Links - Left Aligned */}
          <div className="text-left">
            <h3 className="text-yellow-400 font-bold mb-6 text-lg">Quick Links</h3>
            <nav aria-label="Footer navigation">
              <ul className="space-y-3">
                <li>
                  <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <Link 
                      to="/donate" 
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                      aria-label="Schedule a pickup"
                    >
                      <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">→</span>
                      Schedule Pickup
                    </Link>
                  </motion.div>
                </li>
                <li>
                  <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <Link 
                      to="/wholesale" 
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                      aria-label="Wholesale inquiries"
                    >
                      <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">→</span>
                      Wholesale Inquiry
                    </Link>
                  </motion.div>
                </li>
                <li>
                  <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <Link 
                      to="/impact" 
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                      aria-label="View our environmental and community impact"
                    >
                      <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">→</span>
                      Our Impact
                    </Link>
                  </motion.div>
                </li>
                <li>
                  <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <Link 
                      to="/about" 
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                      aria-label="Learn about our organization"
                    >
                      <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">→</span>
                      About Us
                    </Link>
                  </motion.div>
                </li>
              </ul>
            </nav>
          </div>

          {/* Social Media & Newsletter - Left Aligned */}
          <div className="text-left">
            <h3 className="text-yellow-400 font-bold mb-6 text-lg">Follow Us</h3>
            <div className="flex space-x-4 mb-6">
              {/* Facebook */}
              <motion.a 
                href="https://www.facebook.com/secondchanceclothingorg" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-blue-500 transition-colors duration-300 p-2 rounded-full hover:bg-gray-800"
                whileHover={{ scale: 1.2, y: -2 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Follow us on Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </motion.a>

              {/* Instagram */}
              <motion.a 
                href="https://www.instagram.com/secondchanceclothingorg" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-pink-500 transition-colors duration-300 p-2 rounded-full hover:bg-gray-800"
                whileHover={{ scale: 1.2, y: -2 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Follow us on Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </motion.a>

              {/* X (formerly Twitter) */}
              <motion.a 
                href="https://x.com/SCClothingOrg" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors duration-300 p-2 rounded-full hover:bg-gray-800"
                whileHover={{ scale: 1.2, y: -2 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Follow us on X"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                </svg>
              </motion.a>

              {/* LinkedIn */}
              <motion.a 
                href="https://www.linkedin.com/company/second-chance-clothing-org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-blue-600 transition-colors duration-300 p-2 rounded-full hover:bg-gray-800"
                whileHover={{ scale: 1.2, y: -2 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Follow us on LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </motion.a>
            </div>

            {/* Social Media Description */}
            <div className="mb-4">
              <p className="text-gray-400 text-sm mb-2">Stay updated with our mission</p>
              <div className="text-gray-300 text-sm">
                Follow us on social media for the latest updates on our community impact and sustainability initiatives.
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright Section */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
            <div className="mb-4 md:mb-0">
              <p>&copy; {currentYear} Second Chance Clothing. All rights reserved.</p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end space-x-6 text-xs">
              <Link 
                to="/privacy" 
                className="hover:text-white transition-colors duration-300"
                aria-label="Privacy Policy"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="hover:text-white transition-colors duration-300"
                aria-label="Terms of Service"
              >
                Terms of Service
              </Link>
              <Link 
                to="/accessibility" 
                className="hover:text-white transition-colors duration-300"
                aria-label="Accessibility Statement"
              >
                Accessibility
              </Link>
            </div>
          </div>
          
          {/* Organization Details */}
          <div className="mt-4 text-center md:text-left text-xs text-gray-500">
            <p>Second Chance Clothing is a registered nonprofit organization dedicated to sustainable fashion and community support.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
export const Home = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-black to-gray-800 text-white py-32 min-h-[75vh] flex items-center overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <motion.div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.pexels.com/photos/6994833/pexels-photo-6994833.jpeg')`,
            opacity: 0.3
          }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-6 text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Give Clothes <motion.span 
              className="text-red-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
            >New Life</motion.span>
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl mb-8 text-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            Donate. Reuse. Impact lives.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            <motion.button
              onClick={() => {
                if (window.location.pathname !== '/donate') {
                  window.location.href = '/donate#pickup-form';
                } else {
                  const pickupForm = document.getElementById('pickup-form');
                  if (pickupForm) {
                    pickupForm.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center'
                    });
                  }
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Pickup
            </motion.button>
            <Link to="/wholesale">
              <EnhancedButton variant="secondary">
                Wholesale Inquiry
              </EnhancedButton>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Our Mission</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Second Chance Clothing collects used and recycled clothing from donors, businesses, and recycling programs. 
              We sort, repair, and distribute these items to underserved individuals, shelters, and partner organizations.
            </p>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Collect */}
            <FadeInWhenVisible delay={0.1} className="text-center">
              <motion.div 
                className="bg-red-600 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </motion.div>
              <h3 className="text-xl font-bold text-black mb-2">Collect</h3>
              <p className="text-gray-600">We gather quality recycled clothing from donors across the Bay Area</p>
            </FadeInWhenVisible>

            {/* Sort & Repair */}
            <FadeInWhenVisible delay={0.2} className="text-center">
              <motion.div 
                className="bg-yellow-400 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </motion.div>
              <h3 className="text-xl font-bold text-black mb-2">Sort & Repair</h3>
              <p className="text-gray-600">We carefully sort and repair items to ensure quality for recipients</p>
            </FadeInWhenVisible>

            {/* Distribute */}
            <FadeInWhenVisible delay={0.3} className="text-center">
              <motion.div 
                className="bg-green-600 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </motion.div>
              <h3 className="text-xl font-bold text-black mb-2">Distribute</h3>
              <p className="text-gray-600">We share items with shelters, families, and partner organizations</p>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Making a Difference</h2>
            <p className="text-xl text-gray-300">
              Every donation creates ripples of positive change
            </p>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <FadeInWhenVisible delay={0.1} className="text-center">
              <AnimatedCounter end={50000} suffix="+" className="text-4xl font-bold text-red-600 mb-2" />
              <div className="text-gray-300">Pounds Diverted from Landfills</div>
            </FadeInWhenVisible>
            <FadeInWhenVisible delay={0.2} className="text-center">
              <AnimatedCounter end={2500} suffix="+" className="text-4xl font-bold text-yellow-400 mb-2" />
              <div className="text-gray-300">Families Served</div>
            </FadeInWhenVisible>
            <FadeInWhenVisible delay={0.3} className="text-center">
              <AnimatedCounter end={15} className="text-4xl font-bold text-green-400 mb-2" />
              <div className="text-gray-300">Partner Organizations</div>
            </FadeInWhenVisible>
            <FadeInWhenVisible delay={0.4} className="text-center">
              <motion.div className="text-4xl font-bold text-blue-400 mb-2">Bay Area</motion.div>
              <div className="text-gray-300">Service Region</div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Services Section with Enhanced Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">How We Help</h2>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Pickup Service */}
            <FadeInWhenVisible delay={0.1}>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.img 
                  src="/doorstep-pickup-bags.jpg"
                  alt="Bags of donated toys and clothing ready for pickup"
                  className="w-full h-48 object-cover rounded-lg mb-6"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
                <h3 className="text-xl font-bold text-black mb-4">Doorstep Pickup Service</h3>
                <p className="text-gray-600 mb-4">
                  We come to you! Schedule a pickup, leave your used clothing at your door, 
                  and we'll whisk it away to be reused and loved again.
                </p>
                <motion.button
                  onClick={() => {
                    if (window.location.pathname !== '/donate') {
                      window.location.href = '/donate#pickup-form';
                    } else {
                      const pickupForm = document.getElementById('pickup-form');
                      if (pickupForm) {
                        pickupForm.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center'
                        });
                      }
                    }
                  }}
                  className="text-red-600 hover:text-red-700 font-medium inline-flex items-center group bg-transparent border-none cursor-pointer"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Schedule Pickup 
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.button>
              </motion.div>
            </FadeInWhenVisible>

            {/* Drop-off Bins */}
            <FadeInWhenVisible delay={0.2}>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.img 
                  src="https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/cuqal1pc_IMG_0232.jpeg"
                  alt="Drop-off Bins"
                  className="w-full h-48 object-cover rounded-lg mb-6"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
                <h3 className="text-xl font-bold text-black mb-4">Drop-off Bins</h3>
                <p className="text-gray-600 mb-4">
                  Convenience meets purpose! Our bins are scattered throughout the Bay Area, 
                  ready to catch your gently used clothes anytime.
                </p>
                <Link to="/donate#drop-off-locations">
                  <motion.span
                    className="text-red-600 hover:text-red-700 font-medium inline-flex items-center group"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    Find Locations 
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.span>
                </Link>
              </motion.div>
            </FadeInWhenVisible>

            {/* Wholesale */}
            <FadeInWhenVisible delay={0.3}>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.img 
                  src="https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/cli4xzmw_B924A7FD-E8F1-4FBB-B8CC-239185E94548-min.png"
                  alt="Wholesale Operations"
                  className="w-full h-48 object-cover rounded-lg mb-6"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
                <h3 className="text-xl font-bold text-black mb-4">Wholesale Operations</h3>
                <p className="text-gray-600 mb-4">
                  Partner with us for bulk clothing operations. Minimum 1,000 lb (454 kg) bales 
                  available for wholesale partnerships.
                </p>
                <Link to="/wholesale">
                  <motion.span
                    className="text-red-600 hover:text-red-700 font-medium inline-flex items-center group"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    Learn More 
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.span>
                </Link>
              </motion.div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

// Enhanced Input Component
export const EnhancedInput = ({ label, error, className = '', ...props }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <motion.input
        {...props}
        className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 bg-white
          ${error 
            ? 'border-red-300 focus:border-red-500' 
            : 'border-gray-200 focus:border-red-500 hover:border-gray-300'
          }
          focus:outline-none focus:ring-4 focus:ring-red-100 placeholder-transparent peer
        `}
        placeholder={label}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        whileFocus={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      />
      <motion.label
        className={`absolute left-4 transition-all duration-300 pointer-events-none
          ${props.value || focused 
            ? '-top-2.5 text-sm bg-white px-2 text-red-600' 
            : 'top-3 text-gray-500'
          }
          ${error ? 'text-red-600' : ''}
        `}
        initial={false}
        animate={{
          y: props.value || focused ? -8 : 0,
          scale: props.value || focused ? 0.85 : 1,
        }}
        transition={{ duration: 0.2 }}
      >
        {label}
      </motion.label>
      {error && (
        <motion.p
          className="mt-1 text-sm text-red-600"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

// Reusable CTA Section Component
export const CTASection = () => {
  return (
    <section className="py-16 bg-red-600 text-white relative overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-700 to-red-800"
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        transition={{ duration: 1.5 }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeInWhenVisible>
          <h2 className="text-4xl font-bold mb-4">Ready to Make a Difference?</h2>
          <p className="text-xl mb-8">
            Join us in creating a cycle of care that dresses both people and the earth in hope.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate">
              <EnhancedButton variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                Start Donating Today
              </EnhancedButton>
            </Link>
            <Link to="/about">
              <EnhancedButton variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                Learn Our Story
              </EnhancedButton>
            </Link>
          </div>
        </FadeInWhenVisible>
      </div>
    </section>
  );
};

// Enhanced Select Component
export const EnhancedSelect = ({ label, error, children, className = '', ...props }) => {
  return (
    <div className={`relative ${className}`}>
      <select
        {...props}
        className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-300 bg-white appearance-none
          ${error 
            ? 'border-red-300 focus:border-red-500' 
            : 'border-gray-200 focus:border-red-500 hover:border-gray-300'
          }
          focus:outline-none focus:ring-4 focus:ring-red-100
        `}
      >
        <option value="">{label}</option>
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {error && (
        <motion.p
          className="mt-1 text-sm text-red-600"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

// Valid ZIP codes for donation pickup (outside component for performance)
const VALID_ZIP_CODES = [
  // San Francisco County
  '94102', '94103', '94104', '94105', '94107', '94108', '94109', '94110', '94111', '94112',
  '94114', '94115', '94116', '94117', '94118', '94121', '94122', '94123', '94124', '94127',
  '94129', '94130', '94131', '94132', '94133', '94134', '94158',
  // San Mateo County (partial)
  '94002', '94010', '94011', '94014', '94015', '94016', '94017', '94018', '94019', '94020',
  '94021', '94022', '94023', '94024', '94025', '94026', '94027', '94028', '94030', '94038',
  '94044', '94060', '94061', '94062', '94063', '94064', '94065', '94066', '94070', '94074',
  '94080', '94083', '94401', '94402', '94403', '94404', '94497',
  // Alameda County (partial)
  '94501', '94502', '94536', '94537', '94538', '94539', '94540', '94541', '94542', '94543',
  '94544', '94545', '94546', '94550', '94551', '94552', '94555', '94557', '94560', '94566',
  '94577', '94578', '94579', '94580', '94586', '94587', '94588', '94601', '94602', '94603',
  '94605', '94606', '94607', '94608', '94609', '94610', '94611', '94612', '94613', '94618',
  '94619', '94621', '94702', '94703', '94704', '94705', '94706', '94707', '94708', '94709',
  '94710', '94720'
];

// Donate Page Component
export const Donate = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    zip: ''
  });
  const [formStatus, setFormStatus] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showMailingModal, setShowMailingModal] = useState(false);
  const [mailingEmail, setMailingEmail] = useState('');
  const [errors, setErrors] = useState({});

  const handleMailingSignup = () => {
    setShowMailingModal(true);
  };

  const handleMailingSubmit = (e) => {
    e.preventDefault();
    if (mailingEmail) {
      // Show success with animation
      setFormStatus('mailing-success');
      setShowMailingModal(false);
      setMailingEmail('');
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        if (formStatus === 'mailing-success') {
          setFormStatus('');
        }
      }, 5000);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.street.trim()) newErrors.street = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.zip.trim()) newErrors.zip = 'ZIP code is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!VALID_ZIP_CODES.includes(formData.zip)) {
      setFormStatus('outside-radius');
      setShowCalendar(false);
      return;
    }

    setFormStatus('valid-zip');
    setShowCalendar(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear errors as user types
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
    
    if (formStatus) setFormStatus('');
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <motion.section 
        className="bg-gradient-to-r from-red-600 to-red-800 text-white py-32 min-h-[70vh] flex items-center relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-red-800/20"
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ duration: 1.5 }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Donate Your Clothes
          </motion.h1>
          <motion.p 
            className="text-xl text-red-100 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            Schedule a convenient pickup or find a drop-off location near you. 
            Every donation helps clothe our community and protect our planet.
          </motion.p>
        </div>
      </motion.section>

      {/* Pickup Form Section */}
      <section id="pickup-form" className="py-16 bg-gradient-to-b from-gray-50 to-white" style={{ paddingTop: '6rem', marginTop: '-4rem' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Schedule a Pickup</h2>
            <p className="text-xl text-gray-600">
              We serve San Francisco, San Mateo, Alameda, Contra Costa, Marin, and Santa Clara counties
            </p>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.2}>
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <EnhancedInput
                    type="text"
                    name="name"
                    label="Full Name *"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                  />
                  <EnhancedInput
                    type="email"
                    name="email"
                    label="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <EnhancedInput
                    type="tel"
                    name="phone"
                    label="Phone Number *"
                    value={formData.phone}
                    onChange={handleChange}
                    error={errors.phone}
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <EnhancedInput
                    type="text"
                    name="street"
                    label="Street Address *"
                    value={formData.street}
                    onChange={handleChange}
                    error={errors.street}
                    required
                  />
                </motion.div>

                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <EnhancedInput
                    type="text"
                    name="city"
                    label="City *"
                    value={formData.city}
                    onChange={handleChange}
                    error={errors.city}
                    required
                  />
                  <EnhancedInput
                    type="text"
                    name="zip"
                    label="ZIP Code *"
                    value={formData.zip}
                    onChange={handleChange}
                    error={errors.zip}
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <EnhancedButton 
                    type="submit"
                    variant="primary"
                    className="w-full"
                  >
                    Check Availability
                  </EnhancedButton>
                </motion.div>
              </form>

              {/* Form Status Messages */}
              {formStatus === 'outside-radius' && (
                <motion.div 
                  className="mt-6 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-start">
                    <motion.div
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      className="flex-shrink-0"
                    >
                      <svg className="w-6 h-6 text-yellow-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </motion.div>
                    <div>
                      <h3 className="font-bold text-yellow-800 text-lg">Outside Current Service Radius</h3>
                      <p className="text-yellow-700 mt-2 leading-relaxed">
                        We're not currently serving your area, but we're expanding! 
                        Join our mailing list to be notified when we reach your neighborhood.
                      </p>
                      <motion.button 
                        onClick={handleMailingSignup}
                        className="mt-4 bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Join Mailing List
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}

              {formStatus === 'valid-zip' && showCalendar && (
                <motion.div 
                  className="mt-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center mb-4">
                    <motion.svg 
                      className="w-6 h-6 text-green-500 mr-3" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </motion.svg>
                    <h3 className="font-bold text-green-800 text-lg">Great! We serve your area.</h3>
                  </div>
                  <p className="text-green-700 mb-6 leading-relaxed">
                    You'll receive an email confirmation within 24 hours with pickup details and options to 
                    confirm, cancel, or reschedule.
                  </p>
                  <motion.div 
                    className="bg-white p-6 rounded-lg border border-green-100 shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <h4 className="font-bold text-gray-800 mb-4 text-lg">Preferred Pickup Date</h4>
                    <input
                      type="date"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-100 transition-all duration-300"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <motion.button 
                      className="mt-4 w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Schedule Pickup
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}

              {formStatus === 'mailing-success' && (
                <motion.div 
                  className="mt-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center">
                    <motion.svg 
                      className="w-6 h-6 text-green-500 mr-3" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </motion.svg>
                    <div>
                      <h3 className="font-bold text-green-800 text-lg">Successfully Added to Mailing List!</h3>
                      <p className="text-green-700 mt-1">
                        We'll notify you as soon as we expand service to your area.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Enhanced Mailing List Modal */}
              {showMailingModal && (
                <motion.div 
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setShowMailingModal(false)}
                >
                  <motion.div 
                    className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full"
                    initial={{ scale: 0.9, y: 50 }}
                    animate={{ scale: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-2xl font-bold text-black">Join Our Mailing List</h3>
                      <motion.button 
                        onClick={() => setShowMailingModal(false)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </motion.button>
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      We'll notify you when Second Chance Clothing expands to your area!
                    </p>
                    <form onSubmit={handleMailingSubmit} className="space-y-6">
                      <EnhancedInput
                        type="email"
                        value={mailingEmail}
                        onChange={(e) => setMailingEmail(e.target.value)}
                        label="Email Address *"
                        required
                      />
                      <div className="flex gap-3">
                        <motion.button
                          type="button"
                          onClick={() => setShowMailingModal(false)}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          type="submit"
                          className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold py-3 px-6 rounded-xl transition-all duration-300"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Join List
                        </motion.button>
                      </div>
                    </form>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* What We Accept Section - Enhanced Design */}
      <section id="drop-off-locations" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100" style={{ paddingTop: '6rem', marginTop: '-4rem' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl font-bold text-black mb-6">What We Accept</h2>
              <div className="max-w-3xl mx-auto">
                <p className="text-xl text-gray-600 mb-4">
                  We accept a wide variety of items in good, gently used condition.
                </p>
                <p className="text-lg text-gray-500">
                  Every donation helps create a cycle of care that benefits families in need and protects our environment.
                </p>
              </div>
            </motion.div>
          </FadeInWhenVisible>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: 'Clothing & Apparel', 
                gradient: 'from-red-500 to-red-600',
                hoverGradient: 'from-red-600 to-red-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L8 6v2h8V6l-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                    <path d="M19 8c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1s1-.45 1-1V9c0-.55-.45-1-1-1zM5 8c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1s1-.45 1-1V9c0-.55-.45-1-1-1z"/>
                    <path d="M8 8h8v13H8V8z"/>
                  </svg>
                ),
                items: [
                  'All Season Clothing',
                  'Business & Casual Wear',
                  'Outerwear & Jackets', 
                  'Formal & Special Occasion',
                  'Sleepwear & Undergarments'
                ],
                description: 'Clean, gently worn clothing in all sizes for men, women, and children'
              },
              { 
                title: 'Footwear & Shoes', 
                gradient: 'from-blue-500 to-blue-600',
                hoverGradient: 'from-blue-600 to-blue-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2 18h20c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2zm1.15-3.47L4 13l1.48 1.48c.39.39 1.02.39 1.41 0L8 13.41l1.11 1.11c.39.39 1.02.39 1.41 0L12 13.41l1.11 1.11c.39.39 1.02.39 1.41 0L16 13.41l1.11 1.11c.39.39 1.02.39 1.41 0L20 13l.85 1.48c.25.43.03 1.02-.5 1.22-.14.05-.28.08-.43.08H4.08c-.15 0-.29-.03-.43-.08-.53-.2-.75-.79-.5-1.22z"/>
                    <path d="M22 7v4c0 .55-.45 1-1 1s-1-.45-1-1V8h-2v3c0 .55-.45 1-1 1s-1-.45-1-1V8h-2v3c0 .55-.45 1-1 1s-1-.45-1-1V8H9v3c0 .55-.45 1-1 1s-1-.45-1-1V8H5v3c0 .55-.45 1-1 1s-1-.45-1-1V7c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2z"/>
                  </svg>
                ),
                items: [
                  'Athletic & Running Shoes',
                  'Dress & Professional Shoes',
                  'Boots & Winter Footwear',
                  'Sandals & Summer Shoes',
                  'Children\'s Footwear'
                ],
                description: 'Footwear in good condition with minimal wear and functioning soles'
              },
              { 
                title: 'Accessories & Bags', 
                gradient: 'from-purple-500 to-purple-600',
                hoverGradient: 'from-purple-600 to-purple-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zM10 6a2 2 0 0 1 4 0v1h-4V6zm8 13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v10z"/>
                  </svg>
                ),
                items: [
                  'Handbags & Purses',
                  'Backpacks & Travel Bags',
                  'Belts & Leather Goods'
                ],
                description: 'Fashion accessories and personal items that complete any wardrobe'
              },
              { 
                title: 'Home & Lifestyle', 
                gradient: 'from-green-500 to-green-600',
                hoverGradient: 'from-green-600 to-green-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                ),
                items: [
                  'Bedding & Bed Linens',
                  'Towels & Bath Items',
                  'Toys & Games'
                ],
                description: 'Quality household items and personal belongings for everyday life'
              }
            ].map((category, index) => (
              <FadeInWhenVisible key={index} delay={index * 0.15}>
                <motion.div 
                  className="group relative overflow-hidden bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500"
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  {/* Gradient Header */}
                  <div className={`bg-gradient-to-r ${category.gradient} group-hover:${category.hoverGradient} p-6 transition-all duration-300`}>
                    <div className="flex items-center justify-center mb-4">
                      <div className="text-white transform group-hover:scale-110 transition-transform duration-300">
                        {category.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white text-center mb-2">
                      {category.title}
                    </h3>
                    <p className="text-white/90 text-sm text-center leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                  
                  {/* Content Body */}
                  <div className="p-6">
                    <ul className="space-y-3">
                      {category.items.map((item, itemIndex) => (
                        <motion.li 
                          key={itemIndex}
                          className="flex items-center text-gray-700 text-sm"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: itemIndex * 0.1 }}
                        >
                          <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${category.gradient} mr-3 flex-shrink-0`}></div>
                          <span className="group-hover:text-gray-900 transition-colors duration-300">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </motion.div>
              </FadeInWhenVisible>
            ))}
          </div>
          
          {/* Additional Info Section */}
          <FadeInWhenVisible delay={0.6}>
            <motion.div 
              className="mt-16 bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 rounded-3xl p-8 border border-yellow-200"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center max-w-4xl mx-auto">
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full p-4">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Standards</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  All donated items should be clean, in good working condition, and suitable for redistribution to families in need. 
                  Items with stains, damage, or excessive wear cannot be accepted to ensure quality for our community recipients.
                </p>
                <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span>Clean & Fresh</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span>Gently Used</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span>Functional Condition</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Your Donation Impact</h2>
            <p className="text-xl text-gray-300">
              See how your contributions are making a difference
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-red-600 mb-2">15 lbs</div>
              <div className="text-gray-300">Average donation weight</div>
              <div className="text-sm text-gray-400 mt-2">Keeps textiles out of landfills</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-yellow-400 mb-2">3</div>
              <div className="text-gray-300">Families helped per donation</div>
              <div className="text-sm text-gray-400 mt-2">Direct community impact</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-green-400 mb-2">50%</div>
              <div className="text-gray-300">Reduction in textile waste</div>
              <div className="text-sm text-gray-400 mt-2">Environmental benefit</div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom CTA Section for Donate Page */}
      <section className="py-16 bg-red-600 text-white relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-700 to-red-800"
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ duration: 1.5 }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <h2 className="text-4xl font-bold mb-4">Ready to Make a Difference?</h2>
            <p className="text-xl mb-8">
              Join us in creating a cycle of care that dresses both people and the earth in hope.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                onClick={() => {
                  const pickupForm = document.getElementById('pickup-form');
                  if (pickupForm) {
                    pickupForm.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center'
                    });
                  }
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-red-600 font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Donating Today
              </motion.button>
              <Link to="/about">
                <EnhancedButton variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                  Learn Our Story
                </EnhancedButton>
              </Link>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>
    </div>
  );
};

// Wholesale Page Component
export const Wholesale = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    businessType: '',
    volume: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.businessType) newErrors.businessType = 'Business type is required';
    if (!formData.volume) newErrors.volume = 'Volume requirement is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setShowSuccess(true);
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      businessType: '',
      volume: '',
      message: ''
    });
    setIsSubmitting(false);

    // Hide success message after 5 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear errors as user types
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <motion.section 
        className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-black py-32 min-h-[70vh] flex items-center relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20"
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ duration: 1.5 }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <motion.h1 
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Wholesale Partnerships
            </motion.h1>
            <motion.p 
              className="text-xl max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Partner with Second Chance Clothing for bulk textile operations. 
              Minimum 1,000 lb (454 kg) bales available for wholesale partnerships.
            </motion.p>
          </FadeInWhenVisible>
        </div>
      </motion.section>

      {/* Wholesale Info Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInWhenVisible direction="left" className="text-center lg:text-left">
              <h2 className="text-4xl font-bold text-black mb-6">Bulk Textile Solutions</h2>
              <p className="text-xl text-gray-600 mb-8">
                We supply high-quality, sorted textiles to businesses worldwide. 
                Our rigorous sorting process ensures consistent quality and value for our wholesale partners.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Minimum Order: 1,000 lbs (454 kg)",
                    description: "Compressed bales ready for shipping",
                    delay: 0.1
                  },
                  {
                    title: "Quality Sorted",
                    description: "Professional grading and sorting by category", 
                    delay: 0.2
                  },
                  {
                    title: "Global Shipping",
                    description: "Worldwide delivery options available",
                    delay: 0.3
                  }
                ].map((item, index) => (
                  <FadeInWhenVisible key={index} delay={item.delay}>
                    <motion.div 
                      className="flex items-center justify-center lg:justify-start group"
                      whileHover={{ x: 10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div 
                        className="bg-gradient-to-r from-yellow-400 to-yellow-500 w-12 h-12 rounded-full flex items-center justify-center mr-4 shadow-lg group-hover:shadow-xl"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </motion.div>
                      <div className="text-center lg:text-left">
                        <h3 className="text-lg font-bold text-black">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </motion.div>
                  </FadeInWhenVisible>
                ))}
              </div>
            </FadeInWhenVisible>
            
            <FadeInWhenVisible direction="right">
              <motion.div
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/jdb6obnh_SCC-yellow-forklift-loading-container-daylight.jpeg"
                  alt="Forklift loading compressed textile bales into shipping container with workers in safety vests"
                  className="w-full rounded-2xl shadow-2xl"
                />
              </motion.div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Available Categories Section - Enhanced Professional Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl font-bold text-black mb-6">Available Categories</h2>
              <div className="max-w-3xl mx-auto">
                <p className="text-xl text-gray-600 mb-4">
                  Professionally sorted and quality-graded textile categories for wholesale distribution
                </p>
                <p className="text-lg text-gray-500">
                  Each category meets strict quality standards to ensure consistent, reliable supply for your business needs
                </p>
              </div>
            </motion.div>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: 'Premium Grade A', 
                subtitle: 'Luxury Tier',
                gradient: 'from-emerald-500 to-green-600',
                hoverGradient: 'from-emerald-600 to-green-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ), 
                description: 'Highest quality items in like-new condition with minimal to no signs of wear',
                features: [
                  'Designer & Premium Brands',
                  'Like-New Condition', 
                  'No Visible Wear or Damage',
                  'Complete with Tags/Labels',
                  'Highest Resale Value'
                ],
                minOrder: '500 lbs'
              },
              { 
                title: 'Standard Grade B', 
                subtitle: 'Quality Tier',
                gradient: 'from-blue-500 to-indigo-600',
                hoverGradient: 'from-blue-600 to-indigo-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                ), 
                description: 'Good condition items with minor signs of wear, suitable for retail and distribution',
                features: [
                  'Good Overall Condition',
                  'Minor Wear Acceptable',
                  'Functional & Clean',
                  'Mixed Brand Portfolio',
                  'Excellent Value Proposition'
                ],
                minOrder: '750 lbs'
              },
              { 
                title: 'Vintage & Specialty', 
                subtitle: 'Collector Tier',
                gradient: 'from-purple-500 to-pink-600',
                hoverGradient: 'from-purple-600 to-pink-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                  </svg>
                ), 
                description: 'Unique vintage pieces, designer items, and specialty textiles with collector value',
                features: [
                  'Vintage & Antique Items',
                  'Designer Collections',
                  'Unique/Rare Pieces',
                  'Specialty Fabrics',
                  'Collector Market Appeal'
                ],
                minOrder: '300 lbs'
              },
              { 
                title: 'Bulk Mixed Lots', 
                subtitle: 'Processing Tier',
                gradient: 'from-orange-500 to-red-600',
                hoverGradient: 'from-orange-600 to-red-700',
                icon: (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                  </svg>
                ), 
                description: 'Mixed grade lots ideal for processing, sorting operations, and bulk distribution',
                features: [
                  'Varied Quality Grades',
                  'High Volume Availability',
                  'Processing & Sorting Ready',
                  'Cost-Effective Solution',
                  'Consistent Supply Chain'
                ],
                minOrder: '1,000 lbs'
              }
            ].map((category, index) => (
              <FadeInWhenVisible key={index} delay={index * 0.15}>
                <motion.div 
                  className="group relative overflow-hidden bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500"
                  whileHover={{ y: -15, scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  {/* Premium Gradient Header */}
                  <div className={`bg-gradient-to-r ${category.gradient} group-hover:${category.hoverGradient} p-8 transition-all duration-300`}>
                    <div className="flex flex-col items-center text-center">
                      <div className="text-white/90 mb-3 transform group-hover:scale-110 transition-transform duration-300">
                        {category.icon}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        {category.title}
                      </h3>
                      <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                        {category.subtitle}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content Body */}
                  <div className="p-6">
                    <p className="text-gray-700 mb-6 leading-relaxed text-sm">
                      {category.description}
                    </p>
                    
                    {/* Features List */}
                    <div className="space-y-3 mb-6">
                      <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Key Features</h4>
                      <ul className="space-y-2">
                        {category.features.map((feature, featureIndex) => (
                          <motion.li 
                            key={featureIndex}
                            className="flex items-center text-gray-600 text-sm"
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: featureIndex * 0.1 }}
                          >
                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${category.gradient} mr-3 flex-shrink-0`}></div>
                            <span className="group-hover:text-gray-800 transition-colors duration-300">{feature}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Minimum Order Info */}
                    <div className="border-t border-gray-100 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Min Order</span>
                        <span className="font-bold text-gray-900 text-sm">{category.minOrder}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  
                  {/* Quality Indicator */}
                  <div className="absolute top-4 right-4">
                    <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${category.gradient} animate-pulse`}></div>
                  </div>
                </motion.div>
              </FadeInWhenVisible>
            ))}
          </div>
          
          {/* Wholesale Information Section */}
          <FadeInWhenVisible delay={0.8}>
            <motion.div 
              className="mt-16 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-8 border border-blue-200"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center max-w-4xl mx-auto">
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full p-4">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Partnership Benefits</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Join our global network of wholesale partners and benefit from consistent supply, competitive pricing, 
                  and professional grading standards that ensure quality and reliability for your business operations.
                </p>
                <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    <span>Global Shipping Available</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
                    <span>Quality Guarantee</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                    <span>Flexible Payment Terms</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-pink-500 rounded-full mr-3"></div>
                    <span>Consistent Supply Chain</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Enhanced Inquiry Form Section */}
      <section id="request-quote" className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Request a Quote</h2>
            <p className="text-xl text-gray-600">
              Tell us about your needs and we'll provide a custom quote within 48 hours
            </p>
          </FadeInWhenVisible>

          {showSuccess && (
            <motion.div 
              className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-lg"
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center">
                <motion.svg 
                  className="w-8 h-8 text-green-500 mr-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </motion.svg>
                <div>
                  <h3 className="font-bold text-green-800 text-xl">Thank you for your wholesale inquiry!</h3>
                  <p className="text-green-700 mt-1 text-lg">
                    We will contact you within 2 business days with a detailed quote.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <FadeInWhenVisible delay={0.2}>
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <EnhancedInput
                    type="text"
                    name="companyName"
                    label="Company Name *"
                    value={formData.companyName}
                    onChange={handleChange}
                    error={errors.companyName}
                    required
                  />
                  <EnhancedInput
                    type="text"
                    name="contactName"
                    label="Contact Name *"
                    value={formData.contactName}
                    onChange={handleChange}
                    error={errors.contactName}
                    required
                  />
                </motion.div>

                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <EnhancedInput
                    type="email"
                    name="email"
                    label="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                  />
                  <EnhancedInput
                    type="tel"
                    name="phone"
                    label="Phone Number *"
                    value={formData.phone}
                    onChange={handleChange}
                    error={errors.phone}
                    required
                  />
                </motion.div>

                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <EnhancedSelect
                    name="businessType"
                    label="Select Business Type *"
                    value={formData.businessType}
                    onChange={handleChange}
                    error={errors.businessType}
                    required
                  >
                    <option value="retailer">Retailer</option>
                    <option value="exporter">Exporter</option>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="recycler">Recycler</option>
                    <option value="other">Other</option>
                  </EnhancedSelect>
                  
                  <EnhancedSelect
                    name="volume"
                    label="Monthly Volume Needed *"
                    value={formData.volume}
                    onChange={handleChange}
                    error={errors.volume}
                    required
                  >
                    <option value="1000-5000">1,000 - 5,000 lbs</option>
                    <option value="5000-10000">5,000 - 10,000 lbs</option>
                    <option value="10000-25000">10,000 - 25,000 lbs</option>
                    <option value="25000+">25,000+ lbs</option>
                  </EnhancedSelect>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <div className="relative">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us about specific categories, quality requirements, or other needs..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none focus:ring-4 focus:ring-yellow-100 transition-all duration-300 resize-none"
                    />
                    <label className="absolute left-4 -top-2.5 bg-white px-2 text-sm text-gray-500">
                      Additional Requirements
                    </label>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 shadow-lg hover:shadow-xl
                      ${isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black'
                      }
                    `}
                    whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <motion.div
                          className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full mr-3"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        Submitting...
                      </div>
                    ) : (
                      'Submit Inquiry'
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

// Impact Page Component
export const Impact = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <motion.section 
        className="bg-gradient-to-r from-green-600 via-green-700 to-green-800 text-white py-32 min-h-[70vh] flex items-center relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-green-600/20 to-emerald-600/20"
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ duration: 1.5 }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <motion.h1 
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Our Impact
            </motion.h1>
            <motion.p 
              className="text-xl text-green-100 max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Measuring our progress in creating a more sustainable future 
              through textile recycling and community support.
            </motion.p>
          </FadeInWhenVisible>
        </div>
      </motion.section>

      {/* Environmental Impact */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Environmental Impact</h2>
            <p className="text-xl text-gray-600">
              Every pound of textile we divert makes a difference
            </p>
          </FadeInWhenVisible>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <FadeInWhenVisible direction="left">
              <motion.div
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://images.pexels.com/photos/7289739/pexels-photo-7289739.jpeg"
                  alt="American workers processing clothing distribution with assorted bales in background"
                  className="w-full rounded-2xl shadow-2xl"
                />
              </motion.div>
            </FadeInWhenVisible>
            <div>
              <div className="space-y-8">
                {[
                  {
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    ),
                    bgColor: "bg-green-600",
                    number: 50000,
                    suffix: "+",
                    unit: "lbs",
                    label: "Diverted from landfills",
                    description: "Textiles in landfills can take 200+ years to decompose, releasing harmful greenhouse gases. By diverting textiles, we prevent environmental damage.",
                    delay: 0.1
                  },
                  {
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    ),
                    bgColor: "bg-blue-600",
                    number: 15000,
                    unit: "gallons",
                    label: "Water saved",
                    description: "Textile production is water-intensive. By extending the life of existing garments, we reduce the need for new production and conserve water resources.",
                    delay: 0.2
                  },
                  {
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    ),
                    bgColor: "bg-yellow-500",
                    number: 25,
                    unit: "tons CO2",
                    label: "Emissions prevented",
                    description: "By extending textile lifecycles, we reduce carbon emissions from manufacturing, transportation, and waste processing.",
                    delay: 0.3
                  }
                ].map((item, index) => (
                  <FadeInWhenVisible key={index} delay={item.delay} direction="right">
                    <motion.div 
                      className="text-center md:text-left group"
                      whileHover={{ x: 10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center justify-center md:justify-start mb-4">
                        <motion.div 
                          className={`${item.bgColor} w-16 h-16 rounded-full flex items-center justify-center mr-6 shadow-lg group-hover:shadow-xl`}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.3 }}
                        >
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {item.icon}
                          </svg>
                        </motion.div>
                        <div className="text-center md:text-left">
                          <AnimatedCounter 
                            end={item.number} 
                            suffix={item.suffix || ''} 
                            className="text-4xl font-bold text-black mb-1" 
                          />
                          {item.unit && <span className="text-4xl font-bold text-black"> {item.unit}</span>}
                          <div className="text-gray-600 font-medium">{item.label}</div>
                        </div>
                      </div>
                      <p className="text-gray-600 text-center md:text-left leading-relaxed">
                        {item.description}
                      </p>
                    </motion.div>
                  </FadeInWhenVisible>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Community Impact</h2>
            <p className="text-xl text-gray-600">
              Building stronger communities through clothing access
            </p>
          </FadeInWhenVisible>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                {[
                  {
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    ),
                    bgColor: "bg-red-600",
                    number: 2500,
                    suffix: "+",
                    label: "Families Served",
                    description: "Direct support to Bay Area families in need, providing dignity and confidence through clothing access.",
                    delay: 0.1
                  },
                  {
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    ),
                    bgColor: "bg-yellow-400",
                    number: 15,
                    label: "Partner Shelters",
                    description: "Ongoing partnerships with local shelters and organizations providing clothing for those experiencing homelessness.",
                    delay: 0.2
                  },
                  {
                    icon: (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    ),
                    bgColor: "bg-green-600",
                    number: 150,
                    suffix: "+",
                    label: "Volunteers Engaged",
                    description: "Community members actively participating in sorting, distributing, and supporting our mission.",
                    delay: 0.3
                  }
                ].map((item, index) => (
                  <FadeInWhenVisible key={index} delay={item.delay} direction="left">
                    <motion.div 
                      className="flex items-start group"
                      whileHover={{ x: 10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div 
                        className={`${item.bgColor} w-16 h-16 rounded-full flex items-center justify-center mr-6 mt-1 shadow-lg group-hover:shadow-xl flex-shrink-0`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {item.icon}
                        </svg>
                      </motion.div>
                      <div>
                        <AnimatedCounter 
                          end={item.number} 
                          suffix={item.suffix || ''} 
                          className="text-4xl font-bold text-black mb-2" 
                        />
                        <div className="text-lg font-bold text-gray-800 mb-2">{item.label}</div>
                        <p className="text-gray-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  </FadeInWhenVisible>
                ))}
              </div>
            </div>
            <FadeInWhenVisible direction="right">
              <motion.div
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://images.pexels.com/photos/6995106/pexels-photo-6995106.jpeg"
                  alt="Community volunteers working together"
                  className="w-full rounded-2xl shadow-2xl"
                />
              </motion.div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Partner Organizations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Partner Organizations</h2>
            <p className="text-xl text-gray-600">
              Working together to create lasting community impact
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "SF Homeless Shelter Coalition",
                description: "Providing clothing to individuals experiencing homelessness",
                type: "Shelter Partnership"
              },
              {
                name: "Bay Area Family Services",
                description: "Supporting families in transition with clothing needs",
                type: "Family Support"
              },
              {
                name: "Veterans Community Living Center",
                description: "Serving veterans with clothing and essential items",
                type: "Veterans Support"
              },
              {
                name: "Women's Resource Center",
                description: "Professional clothing for women entering the workforce",
                type: "Career Support"
              },
              {
                name: "Children's Crisis Center",
                description: "Children's clothing for families in crisis situations",
                type: "Child Welfare"
              },
              {
                name: "Senior Community Center",
                description: "Clothing assistance for seniors on fixed incomes",
                type: "Senior Support"
              }
            ].map((partner, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <div className="text-sm font-medium text-red-600 uppercase tracking-wide mb-2">
                  {partner.type}
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{partner.name}</h3>
                <p className="text-gray-600">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">2025 Goals</h2>
            <p className="text-xl text-gray-300">
              Our ambitious targets for expanding impact
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">100K lbs</div>
              <div className="text-gray-300">Textiles diverted from landfills</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">5,000</div>
              <div className="text-gray-300">Families served annually</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-400 mb-2">25</div>
              <div className="text-gray-300">Partner organizations</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">9 Counties</div>
              <div className="text-gray-300">Service area expansion</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

// About Page Component
export const About = () => {
  return (
    <div className="pt-20">
      {/* Hero Section with Image Transition */}
      <motion.section 
        className="relative bg-gradient-to-r from-black via-gray-900 to-gray-800 text-white py-32 min-h-[70vh] flex items-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background Image with Quick Transition */}
        <motion.div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/vp2j8km3_IMG_0264.jpeg')`
          }}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 0.7, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
        />
        
        {/* Dark Overlay for Text Readability */}
        <motion.div 
          className="absolute inset-0 bg-black"
          initial={{ opacity: 0.6 }}
          animate={{ opacity: 0.4 }}
          transition={{ duration: 1, delay: 0.8 }}
        />
        
        {/* Gradient Overlay */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-black/30 to-gray-800/30"
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ duration: 1.5 }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <FadeInWhenVisible>
            <motion.h1 
              className="text-5xl md:text-6xl font-bold mb-6 text-white"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              About Second Chance Clothing
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-200 max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Founded with a passion for sustainability and community impact, 
              we're building a cycle of care that dresses both people and the earth in hope.
            </motion.p>
          </FadeInWhenVisible>
        </div>
      </motion.section>

      {/* Story Section */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInWhenVisible direction="left">
              <h2 className="text-4xl font-bold text-black mb-6">Our Story</h2>
              <div className="space-y-6">
                <motion.p 
                  className="text-lg text-gray-600 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  Founded with a passion for sustainability and community impact, Second Chance Clothing 
                  began as a small initiative to keep wearable clothing out of landfills. What started as 
                  a local effort has grown into a thriving nonprofit, collecting and redistributing used 
                  and recycled clothing to those in need while promoting eco-conscious living.
                </motion.p>
                <motion.p 
                  className="text-lg text-gray-600 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Since our inception, we've diverted thousands of pounds of textiles from waste streams, 
                  weaving a story of renewal with every garment. Our mission is simple yet powerful: 
                  to clothe communities sustainably by rescuing quality garments and giving them a second life.
                </motion.p>
                <motion.p 
                  className="text-lg text-gray-600 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  We envision a world where fashion fuels positive change—reducing waste, supporting people, 
                  and inspiring environmental stewardship, one stitch at a time.
                </motion.p>
              </div>
              
              {/* Call-to-Action */}
              <motion.div 
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Link to="/donate">
                  <EnhancedButton variant="primary" className="mr-4">
                    Join Our Mission
                  </EnhancedButton>
                </Link>
                <Link to="/impact">
                  <EnhancedButton variant="ghost">
                    See Our Impact
                  </EnhancedButton>
                </Link>
              </motion.div>
            </FadeInWhenVisible>
            
            <FadeInWhenVisible direction="right">
              <motion.div
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="https://images.pexels.com/photos/5029919/pexels-photo-5029919.jpeg"
                  alt="Hands coming together for community support"
                  className="w-full rounded-2xl shadow-2xl"
                />
              </motion.div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Our Foundation</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on the principles of sustainability, community, and hope
            </p>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FadeInWhenVisible delay={0.1}>
              <motion.div 
                className="bg-gradient-to-br from-red-50 to-red-100 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-red-100"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="bg-gradient-to-r from-red-600 to-red-700 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </motion.div>
                <h3 className="text-3xl font-bold text-black mb-4">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Our mission is to clothe communities sustainably by rescuing quality garments and giving them a second life. 
                  We collect used and recycled clothing from donors, businesses, and recycling programs, then sort, repair, 
                  and distribute these items to underserved individuals, shelters, and partner organizations.
                </p>
              </motion.div>
            </FadeInWhenVisible>
            
            <FadeInWhenVisible delay={0.2}>
              <motion.div 
                className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-yellow-100"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg className="w-10 h-10 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </motion.div>
                <h3 className="text-3xl font-bold text-black mb-4">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  We envision a world where fashion fuels positive change—reducing waste, supporting people, 
                  and inspiring environmental stewardship, one stitch at a time. A future where every garment 
                  gets a second chance to make a difference in someone's life.
                </p>
              </motion.div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                ),
                title: "Sustainability",
                description: "Every action we take considers environmental impact, from collection to distribution.",
                color: "from-green-500 to-emerald-500",
                bgColor: "from-green-50 to-emerald-50",
                delay: 0.1
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                ),
                title: "Community",
                description: "Building connections and supporting our neighbors through clothing access and dignity.",
                color: "from-blue-500 to-cyan-500", 
                bgColor: "from-blue-50 to-cyan-50",
                delay: 0.2
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                ),
                title: "Innovation",
                description: "Constantly improving our processes to maximize impact and efficiency in textile recovery.",
                color: "from-purple-500 to-pink-500",
                bgColor: "from-purple-50 to-pink-50", 
                delay: 0.3
              }
            ].map((value, index) => (
              <FadeInWhenVisible key={index} delay={value.delay}>
                <motion.div 
                  className={`bg-gradient-to-br ${value.bgColor} p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 text-center`}
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className={`bg-gradient-to-r ${value.color} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {value.icon}
                    </svg>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-black mb-4">{value.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{value.description}</p>
                </motion.div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Our Team</h2>
            <p className="text-xl text-gray-600">
              Meet the passionate individuals driving our mission forward
            </p>
          </FadeInWhenVisible>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Hichem Sarouli',
                title: 'Executive Director',
                image: 'https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/7k76h1mg_IMG_0737.jpeg',
                bio: 'Hichem founded Second Chance Clothing with a vision to make textile recycling both practical and impactful. He continues to guide the organization\'s strategy and partnerships, ensuring that every initiative supports a more responsible and sustainable fashion ecosystem.'
              },
              {
                name: 'Carina Acuña',
                title: 'Operations Manager',
                image: 'https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/iy1oe43c_IMG_0736.jpeg',
                bio: 'Carina manages day-to-day logistics and coordination across all programs. She ensures that collection, sorting, and distribution processes run smoothly, turning community donations into meaningful environmental impact.'
              },
              {
                name: 'Neczar Sarouli',
                title: 'Sustainability Director',
                image: 'https://customer-assets.emergentagent.com/job_upcycled-threads-1/artifacts/mo36238g_IMG_0738.jpeg',
                bio: 'Neczar leads sustainability strategy and global partnerships for Second Chance Clothing. With a background in finance and a focus on measurable impact, he ensures each initiative advances the organization\'s mission of responsible reuse worldwide.'
              }
            ].map((member, index) => (
              <FadeInWhenVisible key={index} delay={index * 0.2}>
                <motion.div 
                  className="bg-gray-50 rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300"
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="relative mb-6"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-white shadow-lg"
                    />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-black mb-2">{member.name}</h3>
                  <p className="text-red-600 font-semibold mb-4 text-lg">{member.title}</p>
                  <p className="text-gray-700 leading-relaxed">{member.bio}</p>
                </motion.div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* Custom CTA Section for About Page */}
      <section className="py-16 bg-red-600 text-white relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-700 to-red-800"
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ duration: 1.5 }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <h2 className="text-4xl font-bold mb-4">Ready to Make a Difference?</h2>
            <p className="text-xl mb-8">
              Join us in creating a cycle of care that dresses both people and the earth in hope.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/donate">
                <EnhancedButton variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                  Start Donating Today
                </EnhancedButton>
              </Link>
              <Link to="/wholesale#request-quote">
                <EnhancedButton variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
                  Request a Quote
                </EnhancedButton>
              </Link>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>
    </div>
  );
};


// Privacy Policy Page Component - Enhanced Design
export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/50">
      {/* Hero Section */}
      <motion.section 
        className="relative pt-32 pb-32 min-h-[70vh] flex items-center bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <motion.div
              className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Legal Information</span>
            </motion.div>
            <motion.h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              className="text-xl text-white/80 max-w-2xl mx-auto mb-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Your privacy matters to us. Learn how we protect and handle your personal information.
            </motion.p>
            <motion.div 
              className="inline-flex items-center text-white/60 text-sm"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Last updated: {new Date().toLocaleDateString()}
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </motion.section>

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents - Sticky Sidebar */}
          <div className="lg:col-span-1">
            <motion.div 
              className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 sticky top-24"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Navigation</h3>
              <nav className="space-y-2">
                {[
                  'Information We Collect',
                  'How We Use Information',
                  'Information Sharing',
                  'Data Security',
                  'Your Rights',
                  'Children\'s Privacy',
                  'Policy Changes',
                  'Contact Us'
                ].map((item, index) => (
                  <motion.a
                    key={index}
                    href={`#section-${index + 1}`}
                    className="block text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm py-1 hover:bg-blue-50 rounded px-2"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {index + 1}. {item}
                  </motion.a>
                ))}
              </nav>
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            <FadeInWhenVisible delay={0.1}>
              <motion.section 
                id="section-1"
                className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full p-3 mr-4">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.5 14.5L6 12l1.41-1.41L10.5 13.67l6.59-6.59L18.5 8.5l-8 8z"/>
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-700 bg-clip-text text-transparent">
                    1. Information We Collect
                  </h2>
                </div>
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p className="text-lg">Second Chance Clothing ("we," "our," or "us") collects information to provide better services to our users and support our mission of textile recycling and community support.</p>
                
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Personal Information You Provide
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white/60 rounded-lg p-4 border border-white/40">
                        <h4 className="font-semibold text-blue-700 mb-2">Donation Pickup</h4>
                        <p className="text-sm text-gray-600">Name, email, phone, address, ZIP code</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 border border-white/40">
                        <h4 className="font-semibold text-indigo-700 mb-2">Wholesale Inquiries</h4>
                        <p className="text-sm text-gray-600">Company details, contact info, business requirements</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 border border-white/40">
                        <h4 className="font-semibold text-purple-700 mb-2">Mailing List</h4>
                        <p className="text-sm text-gray-600">Email for service notifications and updates</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-6 border border-green-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      Information Automatically Collected
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white/60 rounded-lg p-4 border border-white/40">
                        <h4 className="font-semibold text-green-700 mb-2">Usage Data</h4>
                        <p className="text-sm text-gray-600">Pages visited, time spent, click patterns</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 border border-white/40">
                        <h4 className="font-semibold text-teal-700 mb-2">Device Information</h4>
                        <p className="text-sm text-gray-600">Browser type, device, IP address, OS</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-4 border border-white/40">
                        <h4 className="font-semibold text-cyan-700 mb-2">Cookies</h4>
                        <p className="text-sm text-gray-600">User experience and performance analytics</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.section>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.2}>
              <motion.section 
                id="section-2"
                className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-full p-3 mr-4">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-green-700 bg-clip-text text-transparent">
                    2. How We Use Your Information
                  </h2>
                </div>
                <div className="text-gray-700 space-y-6 leading-relaxed">
                  <p className="text-lg">We use the information we collect for the following purposes:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { title: 'Service Operations', desc: 'Processing donation pickups and coordinating collections', icon: '🚚' },
                      { title: 'Business Partnerships', desc: 'Responding to wholesale inquiries and partnership requests', icon: '🤝' },
                      { title: 'Community Updates', desc: 'Sending service area notifications to mailing subscribers', icon: '📧' },
                      { title: 'Website Enhancement', desc: 'Improving functionality and user experience', icon: '🔧' },
                      { title: 'Service Optimization', desc: 'Analyzing usage to optimize our services', icon: '📊' },
                      { title: 'Legal Compliance', desc: 'Meeting legal obligations and protecting rights', icon: '⚖️' }
                    ].map((item, index) => (
                      <motion.div 
                        key={index}
                        className="bg-gradient-to-r from-white/80 to-green-50/80 rounded-lg p-4 border border-green-100"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="flex items-center mb-2">
                          <span className="text-2xl mr-3">{item.icon}</span>
                          <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        </div>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.3}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">3. Information Sharing and Disclosure</h2>
              <div className="text-gray-700 space-y-4">
                <p>We do not sell, trade, or rent your personal information to third parties. We may share information in the following limited circumstances:</p>
                
                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Service Providers</h3>
                <p>We may share information with trusted third-party service providers who assist us in:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Email communication services</li>
                  <li>Website hosting and maintenance</li>
                  <li>Analytics and performance monitoring</li>
                  <li>Donation pickup logistics coordination</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Legal Requirements</h3>
                <p>We may disclose information when required by law, court order, or to protect the rights and safety of Second Chance Clothing, our users, or others.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.4}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">4. Data Security</h2>
              <div className="text-gray-700 space-y-3">
                <p>We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
                <p>However, no internet transmission or electronic storage method is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.5}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">5. Your Rights and Choices</h2>
              <div className="text-gray-700 space-y-4">
                <p>You have the following rights regarding your personal information:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Access:</strong> Request information about what personal data we have about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                  <li><strong>Unsubscribe:</strong> Opt out of mailing lists by clicking unsubscribe links in emails</li>
                  <li><strong>Cookies:</strong> Disable cookies through your browser settings (may affect website functionality)</li>
                </ul>
                <p className="mt-4">To exercise these rights, contact us at <a href="mailto:privacy@secondchanceclothing.org" className="text-red-600 hover:text-red-700">privacy@secondchanceclothing.org</a></p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.6}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">6. Children's Privacy</h2>
              <div className="text-gray-700">
                <p>Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If we discover that a child under 13 has provided us with personal information, we will delete such information immediately.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.7}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">7. Changes to This Privacy Policy</h2>
              <div className="text-gray-700">
                <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.8}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">8. Contact Us</h2>
              <div className="text-gray-700 space-y-2">
                <p>If you have questions about this Privacy Policy, please contact us:</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>Second Chance Clothing</strong></p>
                  <p>Port of SF, Pier 50, Shed A, Bays 28-29</p>
                  <p>San Francisco, CA 94158</p>
                  <p>Email: <a href="mailto:privacy@secondchanceclothing.org" className="text-red-600 hover:text-red-700">privacy@secondchanceclothing.org</a></p>
                  <p>Phone: <a href="tel:+18663854765" className="text-red-600 hover:text-red-700">1-866-385-4765</a></p>
                </div>
              </div>
            </section>
          </FadeInWhenVisible>
          </div>
        </div>

        {/* CTA Section */}
        <CTASection />
      </div>
    </div>
  );
};

// Terms of Service Page Component - Enhanced Design
export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50/30 to-green-100/50">
      {/* Hero Section */}
      <motion.section 
        className="relative pt-32 pb-32 min-h-[70vh] flex items-center bg-gradient-to-r from-emerald-900 via-teal-900 to-green-900 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <motion.div
              className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Legal Agreement</span>
            </motion.div>
            <motion.h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Terms of Service
            </motion.h1>
            <motion.p 
              className="text-xl text-white/80 max-w-2xl mx-auto mb-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Understanding our partnership terms and service agreement for textile recycling and donations.
            </motion.p>
            <motion.div 
              className="inline-flex items-center text-white/60 text-sm"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Last updated: {new Date().toLocaleDateString()}
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </motion.section>

      {/* Streamlined Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Key Terms Overview */}
          <div className="lg:col-span-1">
            <motion.div 
              className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 sticky top-24"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Key Terms</h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Donation Terms</h4>
                  <p className="text-sm text-gray-600">Bay Area pickup service with quality standards</p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Wholesale Orders</h4>
                  <p className="text-sm text-gray-600">Minimum 1,000 lb orders with quality guarantee</p>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">User Rights</h4>
                  <p className="text-sm text-gray-600">Clear responsibilities and liability terms</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Main Terms Content */}
          <div className="lg:col-span-2 space-y-6">

            {[
              {
                title: 'Service Agreement',
                icon: '🤝',
                gradient: 'from-green-500 to-teal-500',
                content: [
                  'By using our website and services, you agree to these terms',
                  'Second Chance Clothing is a nonprofit textile recycling organization',
                  'We provide donation pickup and wholesale textile distribution services'
                ]
              },
              {
                title: 'Donation Terms',
                icon: '📦',
                gradient: 'from-blue-500 to-indigo-500', 
                content: [
                  'Pickup services available within Bay Area ZIP codes',
                  'Items must be clean and in good, usable condition',
                  'Full ownership transfers to Second Chance Clothing upon donation',
                  'Items may be redistributed, recycled, or sold to support our mission'
                ]
              },
              {
                title: 'Wholesale Partnership',
                icon: '🏢',
                gradient: 'from-purple-500 to-pink-500',
                content: [
                  'Minimum order requirement of 1,000 pounds',
                  'Pricing and terms established through partnership agreements',
                  'Quality standards and availability determine order fulfillment',
                  'International shipping available with proper documentation'
                ]
              },
              {
                title: 'User Responsibilities',
                icon: '✅',
                gradient: 'from-orange-500 to-red-500',
                content: [
                  'Provide accurate information in all communications',
                  'Ensure donated items are your property and in good condition',
                  'Be available for scheduled pickup times',
                  'Use our services in compliance with applicable laws'
                ]
              },
              {
                title: 'Limitation of Liability',
                icon: '⚖️',
                gradient: 'from-gray-500 to-slate-500',
                content: [
                  'We are not responsible for items lost or damaged during pickup',
                  'No guarantee of acceptance for all donated items', 
                  'Maximum liability limited to fair market value of affected items',
                  'California law governs this agreement'
                ]
              }
            ].map((section, index) => (
              <FadeInWhenVisible key={index} delay={index * 0.1}>
                <motion.div 
                  className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20"
                  whileHover={{ y: -3, scale: 1.01 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center mb-4">
                    <div className={`bg-gradient-to-r ${section.gradient} rounded-full p-3 mr-4`}>
                      <span className="text-2xl">{section.icon}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{section.title}</h3>
                  </div>
                  <div className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <motion.div 
                        key={itemIndex}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: itemIndex * 0.1 }}
                      >
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${section.gradient} mt-2 mr-3 flex-shrink-0`}></div>
                        <p className="text-gray-700 leading-relaxed">{item}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </FadeInWhenVisible>
            ))}

          <FadeInWhenVisible delay={0.2}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">2. Description of Service</h2>
              <div className="text-gray-700 space-y-4">
                <p>Second Chance Clothing provides the following services:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Donation Pickup Services:</strong> Free collection of used clothing and textiles within our Bay Area service region</li>
                  <li><strong>Drop-off Locations:</strong> Convenient donation bins throughout the Bay Area</li>
                  <li><strong>Wholesale Operations:</strong> Bulk textile supply services for business partners (minimum 1,000 lb orders)</li>
                  <li><strong>Community Support:</strong> Redistribution of quality clothing to shelters, families, and partner organizations</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.3}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">3. Donation Terms and Conditions</h2>
              <div className="text-gray-700 space-y-4">
                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Pickup Service Eligibility</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Pickup services are available within designated Bay Area ZIP codes</li>
                  <li>Items must be in good, usable condition</li>
                  <li>We reserve the right to refuse items that are damaged, soiled, or unsuitable for redistribution</li>
                  <li>Pickup confirmation is subject to route availability and scheduling constraints</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Acceptable Donations</h3>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Clothing (all types and sizes)</li>
                  <li>Shoes and accessories</li>
                  <li>Handbags and belts</li>
                  <li>Bedding and linens</li>
                  <li>Toys (in good condition)</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Transfer of Ownership</h3>
                <p>By donating items to Second Chance Clothing, you transfer full ownership and relinquish all rights to donated materials. Items may be redistributed, recycled, or sold as part of our wholesale operations to support our mission.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.4}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">4. Wholesale Partnership Terms</h2>
              <div className="text-gray-700 space-y-4">
                <p>Wholesale services are subject to the following conditions:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Minimum order requirement of 1,000 pounds (454 kg)</li>
                  <li>All wholesale orders are subject to availability and quality standards</li>
                  <li>Pricing and terms are established through individual partnership agreements</li>
                  <li>International shipping available with appropriate documentation and compliance</li>
                  <li>Payment terms and delivery schedules are negotiated per order</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.5}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">5. User Responsibilities</h2>
              <div className="text-gray-700 space-y-4">
                <p>When using our services, you agree to:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Provide accurate and complete information in all forms and communications</li>
                  <li>Ensure donated items are your property and free from liens or encumbrances</li>
                  <li>Be present during scheduled pickup times or provide alternative arrangements</li>
                  <li>Follow proper donation preparation guidelines (clean, bagged items)</li>
                  <li>Respect our staff and volunteers during all interactions</li>
                  <li>Use our website and services in compliance with applicable laws</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.6}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">6. Limitation of Liability</h2>
              <div className="text-gray-700 space-y-4">
                <p>Second Chance Clothing operates as a nonprofit organization. Our liability is limited as follows:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>We are not responsible for items lost, stolen, or damaged during pickup or processing</li>
                  <li>We do not guarantee acceptance of all donated items</li>
                  <li>We are not liable for scheduling delays or cancellations due to weather, vehicle issues, or other circumstances beyond our control</li>
                  <li>Our maximum liability for any service-related issue shall not exceed the fair market value of affected items</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.7}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">7. Intellectual Property</h2>
              <div className="text-gray-700 space-y-3">
                <p>The Second Chance Clothing website, including all content, features, and functionality, is owned by Second Chance Clothing and is protected by copyright, trademark, and other intellectual property laws.</p>
                <p>You may not reproduce, distribute, modify, or create derivative works from our content without express written permission.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.8}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">8. Modifications to Terms</h2>
              <div className="text-gray-700">
                <p>We reserve the right to modify these Terms of Service at any time. Changes will be posted on this page with an updated "Last modified" date. Continued use of our services after changes constitutes acceptance of the modified terms.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.9}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">9. Governing Law</h2>
              <div className="text-gray-700">
                <p>These Terms of Service are governed by and construed in accordance with the laws of the State of California, without regard to conflict of law principles. Any disputes arising from these terms shall be resolved in the appropriate courts of San Francisco County, California.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={1.0}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">10. Contact Information</h2>
              <div className="text-gray-700 space-y-2">
                <p>For questions about these Terms of Service, please contact us:</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>Second Chance Clothing</strong></p>
                  <p>Port of SF, Pier 50, Shed A, Bays 28-29</p>
                  <p>San Francisco, CA 94158</p>
                  <p>Email: <a href="mailto:legal@secondchanceclothing.org" className="text-red-600 hover:text-red-700">legal@secondchanceclothing.org</a></p>
                  <p>Phone: <a href="tel:+18663854765" className="text-red-600 hover:text-red-700">1-866-385-4765</a></p>
                </div>
              </div>
            </section>
          </FadeInWhenVisible>
          </div>
        </div>

        {/* CTA Section */}
        <CTASection />
      </div>
    </div>
  );
};

// Accessibility Statement Page Component - Enhanced Design
export const AccessibilityStatement = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50/30 to-blue-100/50">
      {/* Hero Section */}
      <motion.section 
        className="relative pt-32 pb-32 min-h-[70vh] flex items-center bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeInWhenVisible>
            <motion.div
              className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Accessibility Commitment</span>
            </motion.div>
            <motion.h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Accessibility Statement
            </motion.h1>
            <motion.p 
              className="text-xl text-white/80 max-w-2xl mx-auto mb-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Our commitment to ensuring digital accessibility for all users, regardless of ability.
            </motion.p>
            <motion.div 
              className="inline-flex items-center text-white/60 text-sm"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Last updated: {new Date().toLocaleDateString()}
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </motion.section>

      {/* Streamlined Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-8">
          {/* Accessibility Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'WCAG 2.1 AA Compliance',
                icon: '✅',
                gradient: 'from-green-500 to-emerald-500',
                description: 'We follow Web Content Accessibility Guidelines for optimal accessibility'
              },
              {
                title: 'Screen Reader Support',
                icon: '🔊',
                gradient: 'from-blue-500 to-cyan-500',
                description: 'Compatible with NVDA, JAWS, VoiceOver, and other assistive technologies'
              },
              {
                title: 'Keyboard Navigation',
                icon: '⌨️',
                gradient: 'from-purple-500 to-indigo-500',
                description: 'Full keyboard navigation support for all interactive elements'
              },
              {
                title: 'High Contrast Design',
                icon: '🎨',
                gradient: 'from-orange-500 to-red-500',
                description: 'Sufficient color contrast ratios and scalable text up to 200%'
              },
              {
                title: 'Alternative Formats',
                icon: '📄',
                gradient: 'from-teal-500 to-green-500',
                description: 'Information available in alternative formats upon request'
              },
              {
                title: 'Continuous Improvement',
                icon: '🔄',
                gradient: 'from-pink-500 to-rose-500',
                description: 'Regular accessibility audits and user testing for ongoing enhancement'
              }
            ].map((feature, index) => (
              <FadeInWhenVisible key={index} delay={index * 0.1}>
                <motion.div 
                  className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20"
                  whileHover={{ y: -5, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center mb-4">
                    <div className={`bg-gradient-to-r ${feature.gradient} rounded-full p-3 mr-4`}>
                      <span className="text-2xl">{feature.icon}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                </motion.div>
              </FadeInWhenVisible>
            ))}

          <FadeInWhenVisible delay={0.2}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Accessibility Standards</h2>
              <div className="text-gray-700 space-y-4">
                <p>Our website strives to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These guidelines explain how to make web content more accessible to people with disabilities.</p>
                
                <h3 className="text-lg font-semibold text-black mt-6 mb-3">WCAG 2.1 AA Compliance Features</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Perceivable:</strong> Content can be perceived by users in different ways</li>
                  <li><strong>Operable:</strong> Interface components and navigation are operable by various methods</li>
                  <li><strong>Understandable:</strong> Information and UI operations are understandable</li>
                  <li><strong>Robust:</strong> Content can be interpreted by a wide variety of assistive technologies</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.3}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Accessibility Features</h2>
              <div className="text-gray-700 space-y-4">
                <p>Our website includes the following accessibility features:</p>
                
                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Visual Accessibility</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>High contrast color schemes with sufficient color contrast ratios</li>
                  <li>Scalable text that can be enlarged up to 200% without loss of functionality</li>
                  <li>Alternative text for all meaningful images and graphics</li>
                  <li>Clear, readable fonts (Poppins Bold and Inter Regular)</li>
                  <li>Visual focus indicators for keyboard navigation</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Navigation and Interaction</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Full keyboard navigation support for all interactive elements</li>
                  <li>Consistent navigation structure across all pages</li>
                  <li>Skip links to main content areas</li>
                  <li>Logical tab order and focus management</li>
                  <li>Clear headings and page structure for screen readers</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Forms and Input</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Descriptive labels for all form fields</li>
                  <li>Clear error messages and validation feedback</li>
                  <li>Required field indicators</li>
                  <li>Sufficient time limits for form completion</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Media and Content</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Descriptive alt text for all images</li>
                  <li>Meaningful link text that describes destination or purpose</li>
                  <li>No content that flashes more than three times per second</li>
                  <li>Structured content using proper HTML semantics</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.4}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Assistive Technology Compatibility</h2>
              <div className="text-gray-700 space-y-4">
                <p>Our website is designed to work with popular assistive technologies, including:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Screen readers (NVDA, JAWS, VoiceOver, TalkBack)</li>
                  <li>Voice recognition software</li>
                  <li>Screen magnification software</li>
                  <li>Switch navigation devices</li>
                  <li>Alternative keyboards and pointing devices</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.5}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Browser and Device Compatibility</h2>
              <div className="text-gray-700 space-y-4">
                <p>Our website is tested and optimized for accessibility across:</p>
                
                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Supported Browsers</h3>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Chrome (current and previous version)</li>
                  <li>Firefox (current and previous version)</li>
                  <li>Safari (current and previous version)</li>
                  <li>Edge (current and previous version)</li>
                </ul>

                <h3 className="text-lg font-semibold text-black mt-6 mb-3">Supported Devices</h3>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Desktop computers</li>
                  <li>Laptops and tablets</li>
                  <li>Mobile phones and devices</li>
                  <li>Various screen sizes and resolutions</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.6}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Known Issues and Limitations</h2>
              <div className="text-gray-700 space-y-4">
                <p>We are continuously working to improve accessibility. Currently known limitations include:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Some animations may not respect user preferences for reduced motion</li>
                  <li>Third-party embedded content may not fully conform to WCAG standards</li>
                  <li>Some complex interactive elements are being evaluated for improved accessibility</li>
                </ul>
                <p className="mt-4">We are actively addressing these issues and welcome feedback on any accessibility barriers you encounter.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.7}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Feedback and Assistance</h2>
              <div className="text-gray-700 space-y-4">
                <p>We welcome feedback about the accessibility of our website. If you encounter accessibility barriers or have suggestions for improvement, please contact us:</p>
                
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p><strong>Accessibility Coordinator</strong></p>
                  <p>Second Chance Clothing</p>
                  <p>Email: <a href="mailto:accessibility@secondchanceclothing.org" className="text-red-600 hover:text-red-700">accessibility@secondchanceclothing.org</a></p>
                  <p>Phone: <a href="tel:+18663854765" className="text-red-600 hover:text-red-700">1-866-385-4765</a></p>
                  <p>Address: Port of SF, Pier 50, Shed A, Bays 28-29, San Francisco, CA 94158</p>
                </div>

                <p>When contacting us about accessibility, please include:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>The specific page or feature you're having trouble with</li>
                  <li>The assistive technology you're using</li>
                  <li>A description of the accessibility barrier</li>
                  <li>Your contact information for follow-up</li>
                </ul>

                <p>We aim to respond to accessibility feedback within 2 business days and will work with you to provide an accessible alternative or solution.</p>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.8}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Alternative Access Options</h2>
              <div className="text-gray-700 space-y-4">
                <p>If you have difficulty accessing any content or functionality on our website, we offer alternative ways to access our services:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Phone Support:</strong> Call 1-866-385-4765 to schedule donation pickups or make wholesale inquiries</li>
                  <li><strong>Email Support:</strong> Contact us at contact@secondchanceclothing.org for assistance with any services</li>
                  <li><strong>In-Person Assistance:</strong> Visit our facility during business hours for direct support</li>
                  <li><strong>Alternative Formats:</strong> We can provide information in alternative formats upon request</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.9}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Ongoing Efforts</h2>
              <div className="text-gray-700 space-y-4">
                <p>Second Chance Clothing is committed to continuous improvement of accessibility:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Regular accessibility audits and testing with assistive technologies</li>
                  <li>Staff training on accessibility best practices and inclusive design</li>
                  <li>User testing with individuals who use assistive technologies</li>
                  <li>Staying current with accessibility standards and best practices</li>
                  <li>Incorporating accessibility considerations into all new developments</li>
                </ul>
              </div>
            </section>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={1.0}>
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">Formal Complaints</h2>
              <div className="text-gray-700 space-y-4">
                <p>If you believe our website does not meet accessibility standards or if you are not satisfied with our response to your accessibility concern, you may file a formal complaint with:</p>
                
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p><strong>U.S. Department of Justice</strong></p>
                  <p>Civil Rights Division</p>
                  <p>950 Pennsylvania Avenue, NW</p>
                  <p>Washington, DC 20530-0001</p>
                  <p>Website: <a href="https://www.ada.gov/" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700">ada.gov</a></p>
                </div>
              </div>
            </section>
          </FadeInWhenVisible>
          </div>
          
          {/* Contact and Feedback Section */}
          <FadeInWhenVisible delay={0.8}>
            <motion.div 
              className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center max-w-3xl mx-auto">
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full p-4">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Accessibility Support</h3>
                <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                  We welcome feedback about accessibility barriers and are committed to providing assistance. 
                  Contact our accessibility coordinator for support or to report issues.
                </p>
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div>
                      <h4 className="font-bold text-purple-800 mb-2">Email Support</h4>
                      <a href="mailto:accessibility@secondchanceclothing.org" className="text-purple-600 hover:text-purple-700 transition-colors">
                        accessibility@secondchanceclothing.org
                      </a>
                    </div>
                    <div>
                      <h4 className="font-bold text-indigo-800 mb-2">Phone Support</h4>
                      <a href="tel:+18663854765" className="text-indigo-600 hover:text-indigo-700 transition-colors">
                        1-866-385-4765
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </FadeInWhenVisible>
        </div>

        {/* CTA Section */}
        <CTASection />
      </div>
    </div>
  );
};
